// Services taxonomy for the organic public site.
// Edit content here; ServiceCategory/ServiceDetail pages render automatically.

export interface ServiceFAQ {
  q: string;
  a: string;
}

export interface ServiceDetail {
  slug: string;
  title: string;
  shortTitle?: string;
  tagline: string;
  summary: string;
  hero: string;
  whoItsFor: string[];
  whatWeDo: string[];
  process: { title: string; description: string }[];
  faqs: ServiceFAQ[];
  meta: { title: string; description: string };
}

export interface ServiceCategory {
  slug: string;
  title: string;
  tagline: string;
  summary: string;
  services: ServiceDetail[];
  meta: { title: string; description: string };
}

const standardProcess = [
  {
    title: "Free confidential consultation",
    description:
      "A CPA on our team reviews your IRS notices, transcripts, and situation — no obligation, no sales pitch.",
  },
  {
    title: "Investigation & IRS transcripts",
    description:
      "We file Form 2848 (Power of Attorney), pull your full IRS account transcripts, and identify every exposure and every option.",
  },
  {
    title: "Resolution strategy",
    description:
      "We design a plan that fits your facts — Offer in Compromise, installment agreement, hardship, abatement, audit defense, or a combination.",
  },
  {
    title: "Negotiation & execution",
    description:
      "We deal with the IRS so you don't have to. We submit, negotiate, and follow through until the case is closed.",
  },
];

export const serviceCategories: ServiceCategory[] = [
  {
    slug: "tax-debt-relief",
    title: "Tax Debt Relief",
    tagline: "Settle, reduce, or restructure what you owe",
    summary:
      "If you owe back taxes you can't pay in full, you have options. We design the right resolution — Offer in Compromise, installment agreement, hardship status, or penalty relief — for your exact situation.",
    meta: {
      title: "Tax Debt Relief Services",
      description:
        "CPA-led tax debt relief: Offer in Compromise, installment agreements, currently not collectible, penalty abatement, innocent spouse relief, and back taxes help.",
    },
    services: [
      {
        slug: "offer-in-compromise",
        title: "Offer in Compromise (OIC)",
        tagline: "Settle IRS debt for less than you owe",
        summary:
          "An Offer in Compromise lets qualifying taxpayers settle IRS tax debt for less than the full amount owed. The IRS accepts roughly one-third of the offers submitted — but the ones built correctly by a credentialed professional have dramatically higher acceptance rates.",
        hero:
          "If paying your full tax debt would create financial hardship — or if doubt exists about whether you actually owe it — an Offer in Compromise may resolve everything for a fraction of the balance.",
        whoItsFor: [
          "Taxpayers who genuinely cannot pay the full balance",
          "People whose income and assets fall below IRS \"reasonable collection potential\"",
          "Cases where collection within the statute is unlikely",
          "Disputed liabilities (doubt as to liability OICs)",
        ],
        whatWeDo: [
          "Run a full pre-qualification analysis before you ever submit",
          "Build Form 656 and Form 433-A (OIC) / 433-B (OIC) correctly",
          "Document allowable expenses to minimize your offer amount",
          "Handle every IRS request and appeal until acceptance",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "What's the IRS acceptance rate for OICs?",
            a: "Historically about 30–40% of submitted Offers in Compromise are accepted. Cases built by experienced tax resolution professionals — with accurate financial disclosures and proper documentation — perform significantly better than self-submitted offers.",
          },
          {
            q: "How long does an OIC take?",
            a: "Most OICs take 6–12 months from submission to final IRS decision. During that time, IRS collection actions are generally paused.",
          },
          {
            q: "What if my OIC is rejected?",
            a: "We can appeal the rejection or pivot to another resolution — installment agreement, currently not collectible status, or a revised offer.",
          },
        ],
        meta: {
          title: "Offer in Compromise (OIC) — Settle IRS Debt for Less",
          description:
            "Settle IRS tax debt for less than you owe. CPA-led Offer in Compromise preparation, submission, and appeals. Free pre-qualification.",
        },
      },
      {
        slug: "installment-agreement",
        title: "IRS Installment Agreement",
        tagline: "Pay what you owe on terms you can afford",
        summary:
          "If you can't pay your full IRS balance today, an installment agreement lets you pay monthly. The right structure can stop collection actions and protect your assets while you pay down the debt.",
        hero:
          "Not everyone qualifies for an Offer in Compromise. For many taxpayers, the smartest move is an installment agreement structured to fit their actual cash flow.",
        whoItsFor: [
          "Taxpayers who can pay over time but not all at once",
          "Anyone facing wage garnishment or levy threats",
          "Businesses with current payroll-tax compliance issues",
          "Cases where partial payment over the statute makes sense (PPIA)",
        ],
        whatWeDo: [
          "Determine the right type — guaranteed, streamlined, partial pay, or non-streamlined",
          "Negotiate the lowest defensible monthly payment",
          "File Form 9465 and supporting Form 433 financials",
          "Get levy releases and lien withdrawals where eligible",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "Will an installment agreement stop wage garnishment?",
            a: "Yes — once an agreement is accepted and in place, the IRS releases active levies and stops new collection actions, as long as you stay current.",
          },
          {
            q: "What if I can't afford even a minimum payment?",
            a: "We pursue a Partial Pay Installment Agreement (PPIA) or Currently Not Collectible status instead.",
          },
        ],
        meta: {
          title: "IRS Installment Agreement — Affordable Monthly Payments",
          description:
            "Set up an IRS installment agreement that fits your budget. CPA-led negotiation to stop levies and protect your income.",
        },
      },
      {
        slug: "currently-not-collectible",
        title: "Currently Not Collectible (CNC)",
        tagline: "Pause IRS collections during financial hardship",
        summary:
          "When paying the IRS would prevent you from meeting basic living expenses, you may qualify for Currently Not Collectible (CNC) status — the IRS stops all collection actions while your account is in hardship.",
        hero:
          "CNC status doesn't erase the debt, but it stops levies, garnishments, and collection calls — giving you breathing room while you rebuild.",
        whoItsFor: [
          "Taxpayers with income at or below IRS allowable living expenses",
          "Anyone facing imminent levy or garnishment",
          "Cases where the IRS collection statute (10 years) may expire during hardship",
        ],
        whatWeDo: [
          "Prepare and submit Form 433-A or 433-F financial disclosure",
          "Document allowable living expenses correctly",
          "Negotiate CNC placement with the assigned Revenue Officer or ACS",
          "Monitor for annual reviews and reinstatement risk",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "Does CNC eliminate my tax debt?",
            a: "No — the debt remains, but the IRS suspends collection. Interest and penalties continue to accrue, and the 10-year collection statute keeps running.",
          },
          {
            q: "How long can I stay in CNC status?",
            a: "Indefinitely, as long as your financial situation justifies it. The IRS reviews periodically based on income changes.",
          },
        ],
        meta: {
          title: "Currently Not Collectible (CNC) — Stop IRS Collections",
          description:
            "Place your IRS account in Currently Not Collectible status to stop levies and garnishments during financial hardship.",
        },
      },
      {
        slug: "penalty-abatement",
        title: "IRS Penalty Abatement",
        tagline: "Remove failure-to-file, failure-to-pay & accuracy penalties",
        summary:
          "IRS penalties often equal 25% or more of the tax owed. First-time abatement and reasonable-cause abatement can wipe them out — if you qualify and request them correctly.",
        hero:
          "Most taxpayers never ask for penalty relief — and the IRS doesn't volunteer it. We identify every eligible penalty and pursue removal.",
        whoItsFor: [
          "Anyone with failure-to-file, failure-to-pay, or accuracy penalties",
          "First-time non-compliance taxpayers (FTA)",
          "Cases with reasonable cause — illness, disaster, reliance on a professional",
        ],
        whatWeDo: [
          "Pull transcripts to identify every penalty assessed",
          "Determine FTA eligibility and reasonable-cause grounds",
          "Draft and submit penalty-abatement requests with supporting evidence",
          "Appeal denials through the IRS Office of Appeals",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "What's First-Time Abatement?",
            a: "FTA waives failure-to-file, failure-to-pay, and failure-to-deposit penalties for taxpayers with a clean compliance history in the prior three years.",
          },
          {
            q: "Can interest be abated?",
            a: "Interest can only be abated in very limited circumstances (IRS error or delay). Penalty interest, however, is abated when the underlying penalty is removed.",
          },
        ],
        meta: {
          title: "IRS Penalty Abatement — Remove Tax Penalties",
          description:
            "Remove IRS penalties through First-Time Abatement or reasonable cause. CPA-led penalty relief and appeals.",
        },
      },
      {
        slug: "innocent-spouse-relief",
        title: "Innocent Spouse Relief",
        tagline: "Don't pay for a spouse's tax mistakes",
        summary:
          "If your spouse or ex-spouse understated taxes on a joint return and you didn't know about it, you may not be responsible for the resulting liability.",
        hero:
          "There are three types of relief — traditional innocent spouse, separation of liability, and equitable relief. We determine which fits your facts and build the case.",
        whoItsFor: [
          "Current or former spouses surprised by joint-return tax debt",
          "Victims of financial control or domestic abuse",
          "Divorced or separated taxpayers facing collection",
        ],
        whatWeDo: [
          "Analyze eligibility under all three relief categories",
          "Prepare and file Form 8857 with supporting documentation",
          "Represent you through the IRS examination and appeals process",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "Is there a deadline?",
            a: "Traditional innocent spouse and separation-of-liability requests must generally be filed within 2 years of the first IRS collection action. Equitable relief has more flexible timing.",
          },
        ],
        meta: {
          title: "Innocent Spouse Relief — IRS Joint Liability Help",
          description:
            "Innocent spouse relief, separation of liability, and equitable relief from joint IRS tax debt. CPA representation.",
        },
      },
      {
        slug: "back-taxes",
        title: "Back Taxes Help",
        tagline: "Resolve unfiled returns and old tax debt",
        summary:
          "Years of unfiled returns or unpaid balances don't fix themselves — they grow. We get you compliant, stop the bleeding, and resolve the underlying debt.",
        hero:
          "The IRS has 10 years to collect, but the clock doesn't start until a return is filed. Getting current is step one of every resolution.",
        whoItsFor: [
          "Taxpayers with multiple years of unfiled returns",
          "Anyone receiving collection notices for old balances",
          "Self-employed individuals behind on estimated payments",
        ],
        whatWeDo: [
          "Reconstruct income from IRS Wage & Income transcripts",
          "Prepare and file all delinquent returns accurately",
          "Negotiate the resolution that best fits the resulting balance",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "How many years of returns do I have to file?",
            a: "IRS policy (Policy Statement 5-133) generally requires the last 6 years to be compliant, though specifics depend on your facts.",
          },
        ],
        meta: {
          title: "Back Taxes Help — Unfiled Returns & Old IRS Debt",
          description:
            "Get caught up on unfiled tax returns and resolve back taxes. CPA-led preparation and IRS negotiation.",
        },
      },
    ],
  },
  {
    slug: "irs-collections-defense",
    title: "IRS Collections Defense",
    tagline: "Stop wage garnishments, levies, and seizures",
    summary:
      "When the IRS moves to collect, time is everything. We move fast to release levies, stop garnishments, and protect your bank accounts, wages, and property.",
    meta: {
      title: "IRS Collections Defense — Stop Wage Garnishments & Levies",
      description:
        "Stop IRS wage garnishments, release bank levies, handle tax liens and asset seizures. CPA-led collections defense, often within days.",
    },
    services: [
      {
        slug: "wage-garnishment",
        title: "Wage Garnishment Release",
        tagline: "Stop the IRS from taking your paycheck",
        summary:
          "An IRS wage garnishment can take up to 70% of your paycheck. We move quickly — most garnishments can be released within days once a resolution is in place.",
        hero:
          "If the IRS has contacted your employer or you've already seen a smaller paycheck, don't wait. Release is possible — but only with an active resolution.",
        whoItsFor: [
          "Anyone with an active IRS wage levy (Form 668-W)",
          "Taxpayers who received Final Notice of Intent to Levy (LT11 / Letter 1058)",
          "Self-employed contractors facing receivable levies",
        ],
        whatWeDo: [
          "Contact the IRS and submit Power of Attorney the same day",
          "Negotiate an installment agreement, CNC, or OIC to enable release",
          "Request and confirm levy release with your employer",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "How fast can a wage garnishment be released?",
            a: "Often within 24–72 hours of putting an acceptable resolution in front of the IRS.",
          },
        ],
        meta: {
          title: "IRS Wage Garnishment Release — Stop Wage Levies",
          description:
            "Stop IRS wage garnishment fast. CPA-led levy release, often within 24–72 hours.",
        },
      },
      {
        slug: "bank-levy",
        title: "Bank Levy Release",
        tagline: "Get your frozen funds back",
        summary:
          "An IRS bank levy freezes your account for 21 days before the bank sends the money to the IRS. That 21-day window is your one chance to act.",
        hero:
          "Bank levies are time-sensitive. The faster we engage the IRS, the better the chance of releasing the funds before they leave your account.",
        whoItsFor: [
          "Anyone with a frozen bank account",
          "Taxpayers who received CP504 or LT11 followed by an account freeze",
          "Business owners facing operating-account levies",
        ],
        whatWeDo: [
          "Same-day Power of Attorney filing with IRS Collections",
          "Demonstrate hardship or negotiate a resolution to obtain release",
          "Coordinate with your bank to confirm release before the 21-day deadline",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "How long do I have to release a bank levy?",
            a: "21 days from when the bank receives the levy. After day 21, the bank is required to send the funds to the IRS.",
          },
        ],
        meta: {
          title: "IRS Bank Levy Release — Unfreeze Your Account",
          description:
            "Release an IRS bank levy before the 21-day deadline. CPA-led emergency representation.",
        },
      },
      {
        slug: "tax-liens",
        title: "Federal Tax Lien Help",
        tagline: "Withdrawal, subordination, and discharge",
        summary:
          "A federal tax lien is the government's legal claim against your property. It destroys credit, blocks home sales, and tanks business financing. There are real options to remove or work around it.",
        hero:
          "Liens can often be withdrawn, subordinated, or discharged depending on your situation and resolution status.",
        whoItsFor: [
          "Homeowners trying to sell or refinance",
          "Business owners needing to borrow",
          "Anyone with a Notice of Federal Tax Lien filed publicly",
        ],
        whatWeDo: [
          "Pursue lien withdrawal under qualifying installment agreements",
          "Apply for lien subordination for refinancing",
          "Apply for lien discharge to clear specific property",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "Will paying off my balance remove the lien?",
            a: "Yes — the IRS releases the lien within 30 days of full payment, and you can request a withdrawal of the public Notice.",
          },
        ],
        meta: {
          title: "Federal Tax Lien Help — Withdrawal & Subordination",
          description:
            "Remove or work around IRS tax liens: withdrawal, subordination, discharge. CPA-led representation.",
        },
      },
      {
        slug: "irs-seizures",
        title: "IRS Asset Seizure Defense",
        tagline: "Protect your home, vehicles, and business assets",
        summary:
          "The IRS rarely seizes physical assets, but when they do, it's serious. We act fast to stop seizure and negotiate a resolution.",
        hero:
          "If you've received a final notice or had assets identified for seizure, every day matters.",
        whoItsFor: [
          "Business owners facing equipment or inventory seizure",
          "Homeowners receiving seizure notices",
          "Anyone with significant IRS exposure and physical assets",
        ],
        whatWeDo: [
          "Emergency engagement with IRS Collection",
          "File Collection Due Process appeals where eligible",
          "Negotiate alternative resolution to halt seizure",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "Can the IRS take my house?",
            a: "Legally yes, but it requires approval up the chain and is extremely rare. We work to ensure it never reaches that point.",
          },
        ],
        meta: {
          title: "IRS Asset Seizure Defense",
          description:
            "Stop IRS seizure of homes, vehicles, equipment, and business assets. CPA-led emergency defense.",
        },
      },
      {
        slug: "stop-collections",
        title: "Stop IRS Collections",
        tagline: "Halt aggressive IRS collection action",
        summary:
          "When the IRS is calling your home, calling your employer, or sending letters week after week, there are tools to pause everything while a real resolution is built.",
        hero:
          "Collection Due Process appeals, Currently Not Collectible status, and pending resolution agreements all create collection holds.",
        whoItsFor: [
          "Anyone in active IRS collection",
          "Taxpayers with Revenue Officer contact",
          "Cases that need time to investigate the right resolution",
        ],
        whatWeDo: [
          "File Power of Attorney to redirect IRS contact to our office",
          "Use CDP appeals, hardship requests, and pending agreements to trigger holds",
          "Build the long-term resolution behind the scenes",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "Once you take over, will the IRS stop contacting me?",
            a: "Yes — after Form 2848 is filed, the IRS is required to contact us, not you.",
          },
        ],
        meta: {
          title: "Stop IRS Collections — Halt Aggressive Collection",
          description:
            "Stop IRS collection calls, letters, and Revenue Officer visits. CPA-led representation and collection holds.",
        },
      },
    ],
  },
  {
    slug: "audit-defense",
    title: "Audit & Examination Defense",
    tagline: "We respond, represent, and negotiate so you never have to",
    summary:
      "An IRS audit is winnable when it's handled by a credentialed professional from day one. We respond to the auditor, control the scope, and protect your records.",
    meta: {
      title: "IRS Audit Defense & Examination Representation",
      description:
        "CPA-led IRS audit representation, CP2000 response, appeals, and examination reconsideration. Don't face the IRS alone.",
    },
    services: [
      {
        slug: "irs-audit-representation",
        title: "IRS Audit Representation",
        tagline: "Full audit defense — correspondence, office, and field audits",
        summary:
          "You're not required to meet with an IRS auditor. With a Power of Attorney in place, we attend in your stead, control document production, and protect your interests.",
        hero:
          "Most taxpayers say too much, produce too much, or miss deadlines — turning a narrow audit into a multi-year exam. We prevent that.",
        whoItsFor: [
          "Anyone who received an audit notice (Letter 525, 566, 915, or CP75)",
          "Self-employed taxpayers under Schedule C audit",
          "Businesses under examination",
          "High-income taxpayers under audit",
        ],
        whatWeDo: [
          "Take over all auditor communication via Power of Attorney",
          "Control document production and scope",
          "Negotiate at the examination level to avoid appeals",
          "Pursue appeals or Tax Court when needed",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "Should I just send the IRS what they ask for?",
            a: "Not without review. Many audits expand because taxpayers volunteer documents that open new issues. Every document should be vetted before it leaves your hands.",
          },
        ],
        meta: {
          title: "IRS Audit Representation — CPA Defense",
          description:
            "CPA-led IRS audit representation for correspondence, office, and field audits. Don't face the IRS alone.",
        },
      },
      {
        slug: "irs-appeals",
        title: "IRS Appeals",
        tagline: "Independent review of unfavorable IRS decisions",
        summary:
          "The IRS Office of Appeals is independent of examination and collection. It's often the best forum to resolve disputes without litigation.",
        hero:
          "Appeals officers have settlement authority that examiners and revenue officers don't. Many cases that look hopeless at exam are winnable here.",
        whoItsFor: [
          "Anyone disputing audit results",
          "CDP appeal candidates (Collection Due Process)",
          "Cases where the IRS rejected an OIC or installment agreement",
        ],
        whatWeDo: [
          "Draft formal written protests",
          "Present and negotiate at Appeals conferences",
          "Hazards-of-litigation analysis to support settlement",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "What's the deadline to appeal?",
            a: "Usually 30 days from the IRS letter giving appeal rights — but specific deadlines vary by notice type. Don't miss them.",
          },
        ],
        meta: {
          title: "IRS Appeals — Office of Appeals Representation",
          description:
            "Independent IRS Appeals representation for audit, collection, and rejected-resolution cases.",
        },
      },
      {
        slug: "cp2000-response",
        title: "CP2000 Response",
        tagline: "Don't let the IRS auto-assess income you don't actually owe",
        summary:
          "A CP2000 says the IRS found income that doesn't match your return. The auto-adjustments are often wrong — but if you don't respond correctly within 30 days, they become permanent.",
        hero:
          "CP2000s are not audits — they're proposed assessments. They can be reduced or eliminated with proper documentation.",
        whoItsFor: [
          "Anyone who received a CP2000",
          "Taxpayers with 1099 reporting discrepancies",
          "Cases involving cost-basis adjustments on investments",
        ],
        whatWeDo: [
          "Reconcile reported income against your records",
          "Identify offsetting basis, expenses, or credits",
          "Draft a documented written response",
          "Escalate to Appeals if needed",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "What if I ignore a CP2000?",
            a: "The IRS will send a Statutory Notice of Deficiency (90-day letter) and assess the full proposed tax, plus penalties and interest.",
          },
        ],
        meta: {
          title: "CP2000 Response — IRS Underreporter Notice Help",
          description:
            "Respond to IRS CP2000 underreporter notices correctly. CPA-led written responses and Appeals representation.",
        },
      },
      {
        slug: "exam-reconsideration",
        title: "Examination Reconsideration",
        tagline: "Reopen a closed audit when new evidence exists",
        summary:
          "If you missed an audit, didn't respond, or have new evidence, audit reconsideration may be available — even after the assessment is final.",
        hero:
          "Audit reconsideration is one of the most underused tools in the IRS playbook.",
        whoItsFor: [
          "Taxpayers assessed by default (no response audit)",
          "Anyone with newly discovered documentation",
          "Cases where the original auditor disallowed legitimate items",
        ],
        whatWeDo: [
          "Evaluate eligibility and likelihood of reversal",
          "Prepare the reconsideration package",
          "Negotiate with the reconsideration examiner",
        ],
        process: standardProcess,
        faqs: [],
        meta: {
          title: "IRS Examination Reconsideration",
          description:
            "Reopen a closed IRS audit with audit reconsideration. CPA-led representation.",
        },
      },
    ],
  },
  {
    slug: "business-tax",
    title: "Business Tax Resolution",
    tagline: "Protect your business from IRS shutdown risk",
    summary:
      "Payroll-tax problems can end a business overnight. We resolve 941/940 liabilities, defend against Trust Fund Recovery Penalty assessment, and keep operations running.",
    meta: {
      title: "Business Tax Resolution — Payroll Tax & Trust Fund",
      description:
        "Resolve business payroll tax problems, 941/940 liabilities, Trust Fund Recovery Penalty, sales tax, and business audits.",
    },
    services: [
      {
        slug: "payroll-tax",
        title: "Payroll Tax Problems",
        tagline: "Resolve 941 and 940 liabilities before they shut you down",
        summary:
          "Unpaid payroll taxes are the IRS's highest collection priority. Revenue Officers move fast, and the penalties stack up quickly. The path forward exists — but it requires immediate, expert action.",
        hero:
          "If you're behind on payroll taxes, you need a CPA who handles these cases regularly. This is not the time for general representation.",
        whoItsFor: [
          "Businesses with unpaid 941 or 940 deposits",
          "Companies with assigned IRS Revenue Officers",
          "Owners facing personal Trust Fund Recovery Penalty assessment",
        ],
        whatWeDo: [
          "Stop further accrual through current compliance",
          "Negotiate installment agreements or in-business CNC",
          "Defend against TFRP personal assessment",
          "Restructure payroll processes to prevent recurrence",
        ],
        process: standardProcess,
        faqs: [
          {
            q: "Can the IRS shut down my business?",
            a: "For payroll-tax non-compliance, yes — the IRS can seize assets and pursue federal injunctions. Engagement before that point is critical.",
          },
        ],
        meta: {
          title: "Payroll Tax Problems — 941 / 940 Resolution",
          description:
            "Resolve unpaid payroll tax (941, 940) liabilities. CPA-led business tax resolution and TFRP defense.",
        },
      },
      {
        slug: "trust-fund-recovery",
        title: "Trust Fund Recovery Penalty Defense",
        tagline: "Don't let business payroll debt follow you personally",
        summary:
          "The Trust Fund Recovery Penalty (TFRP) allows the IRS to assess unpaid payroll taxes personally against responsible individuals — owners, officers, even bookkeepers. We fight assessment and reduce liability.",
        hero:
          "TFRP follows you even if the business closes. Defending the Form 4180 interview is critical.",
        whoItsFor: [
          "Owners and officers facing Letter 1153 / Form 2751",
          "Anyone scheduled for a Form 4180 interview",
          "Already-assessed individuals seeking reduction or abatement",
        ],
        whatWeDo: [
          "Represent you at the Form 4180 responsible-person interview",
          "Protest TFRP assessments through IRS Appeals",
          "Pursue reasonable-cause defenses",
        ],
        process: standardProcess,
        faqs: [],
        meta: {
          title: "Trust Fund Recovery Penalty (TFRP) Defense",
          description:
            "Defend against IRS Trust Fund Recovery Penalty assessment. CPA-led Form 4180 interview representation.",
        },
      },
      {
        slug: "employment-tax",
        title: "Employment Tax (941 / 940) Resolution",
        tagline: "Comprehensive employment-tax compliance and resolution",
        summary:
          "From past-due 941s to W-2 / 1099 worker-classification disputes, we resolve the full range of employment-tax issues.",
        hero:
          "Worker classification audits and SS-8 determinations can create enormous unexpected liabilities. We defend on both classification and quantum.",
        whoItsFor: [
          "Employers behind on 941 / 940 filings",
          "Companies facing worker-classification audits",
          "Businesses receiving SS-8 inquiries",
        ],
        whatWeDo: [
          "Prepare and file delinquent employment-tax returns",
          "Defend worker-classification audits",
          "Negotiate resolution of resulting liabilities",
        ],
        process: standardProcess,
        faqs: [],
        meta: {
          title: "Employment Tax Resolution — 941 / 940 / Worker Classification",
          description:
            "Resolve employment-tax issues: 941 / 940 returns, worker classification audits, SS-8 disputes.",
        },
      },
      {
        slug: "sales-and-use-tax",
        title: "Sales & Use Tax Resolution",
        tagline: "State sales-tax audits, assessments, and voluntary disclosure",
        summary:
          "Sales-tax exposure can be devastating, especially post-Wayfair. We resolve state sales-tax assessments, manage voluntary disclosure programs, and defend audits.",
        hero:
          "State revenue departments are aggressive. Coordinated CPA representation prevents a single audit from cascading across jurisdictions.",
        whoItsFor: [
          "Businesses under state sales-tax audit",
          "Companies with multi-state exposure",
          "Sellers facing nexus assessments",
        ],
        whatWeDo: [
          "Represent in state audits and appeals",
          "Negotiate voluntary disclosure agreements",
          "Defend against nexus and use-tax assessments",
        ],
        process: standardProcess,
        faqs: [],
        meta: {
          title: "Sales & Use Tax Resolution — Multi-State Audit Help",
          description:
            "Resolve state sales-tax audits, assessments, and voluntary disclosures. CPA-led representation.",
        },
      },
      {
        slug: "tax-preparation",
        title: "Business Tax Preparation",
        tagline: "Get current and stay compliant",
        summary:
          "Resolution begins with compliance. We prepare delinquent business returns — 1120, 1120-S, 1065 — accurately, and keep your business compliant going forward.",
        hero:
          "Filing correctly is the foundation of every successful resolution. We get it right the first time.",
        whoItsFor: [
          "Businesses with unfiled returns",
          "Companies needing amended returns post-audit",
          "Owners seeking ongoing CPA preparation",
        ],
        whatWeDo: [
          "Prepare delinquent business returns",
          "File amended returns to correct errors",
          "Provide ongoing year-round CPA preparation",
        ],
        process: standardProcess,
        faqs: [],
        meta: {
          title: "Business Tax Preparation — CPA-Prepared 1120 / 1120-S / 1065",
          description:
            "CPA-led business tax preparation, delinquent returns, and amended filings.",
        },
      },
    ],
  },
];

export function getServiceCategory(slug: string): ServiceCategory | undefined {
  return serviceCategories.find((c) => c.slug === slug);
}

export function getServiceDetail(
  categorySlug: string,
  serviceSlug: string,
): { category: ServiceCategory; service: ServiceDetail } | undefined {
  const category = getServiceCategory(categorySlug);
  if (!category) return undefined;
  const service = category.services.find((s) => s.slug === serviceSlug);
  if (!service) return undefined;
  return { category, service };
}

export function getAllServicePaths(): { categorySlug: string; serviceSlug: string }[] {
  return serviceCategories.flatMap((c) =>
    c.services.map((s) => ({ categorySlug: c.slug, serviceSlug: s.slug })),
  );
}
